import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PageShell } from "@/components/site/Site";
import Payment from "@/components/site/Payment";
import { GiftToggle, GiftFields, giftValid } from "@/components/site/Gift";
import { api, errMsg, useContent, usePayConfig, regionPrices, SUPPORT_EMAIL } from "@/lib/api";

const money = (n) => Number(n).toFixed(2);
const looksLikeInitials = (s) => {
  const t = s.trim().split(/[\s.]+/).filter(Boolean);
  return t.length > 0 && t.every((x) => x.length === 1);
};
const STEPS = ["Your name", "Email", "Confirm"];

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-sm font-medium text-stone-800">{label}</Label>
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-2 text-xs leading-relaxed text-stone-500">{hint}</p>}
  </div>
);

const TypeOption = ({ value, current, onPick, title, desc }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={`first-type-${value}`}
    className={`flex-1 border p-4 text-left transition-colors ${current === value ? "border-stone-900 bg-white" : "border-[#E6DEC8] hover:border-stone-400"}`}>
    <p className="text-sm font-semibold">{title}</p>
    <p className="mt-1 text-xs text-stone-500">{desc}</p>
  </button>
);

function StepFirst({ s, set }) {
  const onFirst = (v) => set({ first: v, firstType: s.typeTouched ? s.firstType : looksLikeInitials(v) ? "initials" : "full" });
  return (
    <div className="space-y-8">
      <Field label="First name or initials *" hint="Your first name, or the initials you use in place of a first name — exactly as you write it, e.g. Ravi, R., R. K. or J. P.">
        <Input value={s.first} onChange={(e) => onFirst(e.target.value)} className="h-14 bg-white font-serif-display text-2xl md:text-2xl" maxLength={80} data-testid="first-name-input" autoFocus />
      </Field>
      <div>
        <p className="text-sm font-medium text-stone-800">What does this represent?</p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <TypeOption value="full" current={s.firstType} onPick={(v) => set({ firstType: v, typeTouched: true })} title="My full first name" desc="e.g. Ravi, Maria, Jean-Luc" />
          <TypeOption value="initials" current={s.firstType} onPick={(v) => set({ firstType: v, typeTouched: true })} title="Initials I use as my first name" desc="e.g. R. K. — never expanded into names" />
        </div>
      </div>
    </div>
  );
}

function StepOther({ s, set }) {
  const upd = (i, v) => set({ middles: s.middles.map((m, j) => (j === i ? v : m)) });
  return (
    <div className="space-y-8">
      <Field label="Middle name (if any)" hint="Leave blank if you have no middle name. Add another field only if you have more than one.">
        <div className="space-y-3">
          {s.middles.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input value={m} onChange={(e) => upd(i, e.target.value)} className="h-12 bg-white" maxLength={80} data-testid={`middle-name-input-${i}`} />
              <button type="button" onClick={() => set({ middles: s.middles.filter((_, j) => j !== i) })} className="px-3 text-stone-500 hover:text-stone-900" data-testid={`remove-middle-${i}`} aria-label="Remove"><X className="h-4 w-4" /></button>
            </div>
          ))}
          {s.middles.length < 6 && (
            <button type="button" onClick={() => set({ middles: [...s.middles, ""] })} className="inline-flex items-center gap-2 text-sm text-[#8B5A2B] hover:text-stone-900" data-testid="add-middle-btn">
              <Plus className="h-4 w-4" /> Add another middle name
            </button>
          )}
        </div>
      </Field>
      <Field label="Surname or family name" hint="Compound and multi-part surnames are welcome, e.g. García Márquez or Smith-Jones.">
        <Input value={s.surname} disabled={s.noSurname} onChange={(e) => set({ surname: e.target.value })} className="h-12 bg-white" maxLength={120} data-testid="surname-input" />
      </Field>
      <label className="flex items-center gap-3 text-sm text-stone-700">
        <Checkbox checked={s.noSurname} onCheckedChange={(v) => set({ noSurname: !!v, surname: v ? "" : s.surname })} data-testid="no-surname-checkbox" />
        My naming convention does not include a family name
      </label>
    </div>
  );
}

function PromoField({ s, set }) {
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try { const r = (await api.post("/promo/check", { code: s.promoInput, currency: s.currency })).data; set({ promo: r }); toast.success(`Code applied: ${r.currency} ${r.discount} off`); }
    catch (e) { set({ promo: null }); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Field label="Promotional code (optional)">
      <div className="flex gap-2">
        <Input value={s.promoInput} onChange={(e) => set({ promoInput: e.target.value.toUpperCase(), promo: null })} className="h-12 max-w-xs bg-white uppercase" maxLength={30} data-testid="promo-input" />
        <button type="button" onClick={apply} disabled={!s.promoInput.trim() || busy} className="border border-stone-900 px-5 text-sm disabled:opacity-40" data-testid="promo-apply-btn">Apply</button>
      </div>
      {s.promo && <p className="mt-2 text-xs text-emerald-700" data-testid="promo-applied">{s.promo.code}: {s.promo.currency} {money(s.promo.discount)} off — you pay {s.promo.currency} {money(s.promo.final)}</p>}
    </Field>
  );
}

function IndiaNote() {
  const [f, setF] = useState({ email: "", name: "", website: "" });
  const [done, setDone] = useState(false);
  const join = async (e) => {
    e.preventDefault();
    try { await api.post("/waitlist", f); setDone(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="border-l-2 border-[#B8860B] bg-white/60 px-4 py-4 text-sm leading-relaxed text-stone-700" data-testid="india-payment-note">
      <p>Online payment for India opens shortly. Thank you for your patience — for early access, please write to <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> or use our <a href="/contact" className="underline">contact page</a>.</p>
      {done ? <p className="mt-3 font-medium text-emerald-800" data-testid="waitlist-done">Thank you — we'll email you the moment payments for India open.</p> : (
        <form onSubmit={join} className="mt-4" data-testid="waitlist-form">
          <p className="font-serif-display text-lg italic text-[#5B2C36]">Be the first to know</p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Input type="email" required placeholder="Your email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 bg-white" data-testid="waitlist-email-input" />
            <Input placeholder="Name (optional)" maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 bg-white sm:max-w-[11rem]" data-testid="waitlist-name-input" />
            <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="hidden" aria-hidden="true" />
            <button className="h-11 shrink-0 rounded-full bg-stone-900 px-5 text-sm text-white hover:bg-[#5B2C36]" data-testid="waitlist-submit-btn">Notify me</button>
          </div>
        </form>
      )}
    </div>
  );
}

function StepEmail({ s, set, prices, indiaClosed }) {
  const match = s.email && s.email.toLowerCase() === s.email2.toLowerCase();
  return (
    <div className="space-y-8">
      <Field label={s.isGift ? "Your email address *" : "Email address *"} hint={s.isGift ? "Your receipt and a note when the gift is delivered are sent here." : "Your report will be delivered here."}><Input type="email" value={s.email} onChange={(e) => set({ email: e.target.value })} className="h-12 bg-white" data-testid="email-input" /></Field>
      <Field label="Confirm email address *">
        <Input type="email" value={s.email2} onChange={(e) => set({ email2: e.target.value })} onPaste={(e) => e.preventDefault()} className="h-12 bg-white" data-testid="email-confirm-input" />
        {s.email2 && <p className={`mt-2 text-xs ${match ? "text-emerald-700" : "text-[#5B2C36]"}`} data-testid="email-match-indicator">{match ? "Email addresses match" : "Email addresses do not match"}</p>}
      </Field>
      {s.isGift && <GiftFields s={s} set={set} />}
      <Field label="Price">
        <div className="flex flex-wrap gap-3">
          {prices.map((p) => (
            <button type="button" key={p.currency} onClick={() => set({ currency: p.currency, promo: null })} data-testid={`currency-${p.currency}`}
              className={`border px-5 py-3 text-sm transition-colors ${s.currency === p.currency ? "border-stone-900 bg-white" : "border-[#E6DEC8]"}`}>
              {p.currency} {p.amount} · {p.provider === "razorpay" ? "Razorpay" : "PayPal"}
            </button>
          ))}
        </div>
      </Field>
      {indiaClosed && <IndiaNote />}
      <PromoField s={s} set={set} />
    </div>
  );
}

const PART_LABEL = { first: "First name", middle: "Middle name", family: "Family name" };

function StepConfirm({ s, set, preview }) {
  if (!preview) return <p className="text-stone-500">Checking your name…</p>;
  return (
    <div className="space-y-8">
      <div className="border border-stone-900 bg-white p-8">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">Name to be analysed</p>
        <p className="mt-3 break-words font-serif-display text-4xl sm:text-5xl" data-testid="confirm-display-name">{preview.display_name}</p>
        <table className="mt-6 w-full text-sm" data-testid="confirm-components-table">
          <tbody>
            {preview.components.map((c, i) => (
              <tr key={i} className="border-t border-[#E6DEC8]">
                <td className="py-3 text-stone-500">{PART_LABEL[c.name_part]}{c.name_part === "middle" ? ` ${c.part_index + 1}` : ""}</td>
                <td className="py-3 font-medium">{c.text}</td>
                <td className="py-3 text-right text-xs uppercase tracking-wider text-[#8B5A2B]">{c.type === "initials" ? `Initials: ${c.initials.join(" · ")}` : c.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {preview.notes.map((n, i) => <p key={i} className="mt-4 border-l-2 border-[#B8860B] pl-3 text-xs text-stone-600">{n}</p>)}
      </div>
      {s.isGift
        ? <p className="text-sm text-stone-600" data-testid="confirm-gift">A gift from <strong>{s.giftFrom}</strong>, delivered to <strong data-testid="confirm-email">{s.recipientEmail}</strong>. Your receipt goes to {s.email}.</p>
        : <p className="text-sm text-stone-600">Report delivered to <strong data-testid="confirm-email">{s.email}</strong></p>}
      <label className="flex items-start gap-3 text-sm leading-relaxed text-stone-700">
        <Checkbox checked={s.confirmed} onCheckedChange={(v) => set({ confirmed: !!v })} className="mt-0.5" data-testid="confirm-name-checkbox" />
        I confirm this is exactly how my name is spelt and structured. I understand that the name entered determines the interpretation and cannot be changed after the report is written.
      </label>
    </div>
  );
}

const Done = ({ order, email }) => (
  <div className="fade-up border border-stone-900 bg-white p-10" data-testid="order-created">
    <Check className="h-8 w-8 text-[#B8860B]" />
    <h2 className="mt-4 font-serif-display text-4xl" data-testid="order-heading">{order.payment_status === "paid" ? "Your order is confirmed" : "Complete your order"}</h2>
    <p className="mt-2 text-stone-600">Reference <span className="font-mono-ui text-stone-900" data-testid="order-ref">{order.ref}</span> · {order.currency} {money(order.amount)}{order.discount > 0 && <span className="text-emerald-700"> (saved {order.currency} {money(order.discount)})</span>}</p>
    <a href={`/receipt?ref=${encodeURIComponent(order.ref)}&email=${encodeURIComponent(email)}`} className="mt-2 inline-block text-sm text-[#8B5A2B] underline underline-offset-4" data-testid="receipt-link">Your order page (bookmark it)</a>
    <Payment order={order} email={email} />
  </div>
);

export default function Explore() {
  const { data } = useContent();
  const { data: cfg } = usePayConfig();
  const prices = regionPrices((data?.prices || []).filter((p) => p.active), cfg);
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [order, setOrder] = useState(null);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step, order]);
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState({ first: "", firstType: "full", typeTouched: false, middles: [""], surname: "", noSurname: false, email: "", email2: "", currency: "INR", confirmed: false, promoInput: "", promo: null, isGift: false, recipientEmail: "", recipientEmail2: "", giftFrom: "", giftMessage: "" });
  const set = (p) => setS((o) => ({ ...o, ...p }));
  const payload = { first: s.first, first_type: s.firstType, middles: s.middles.filter((m) => m.trim()), surname: s.surname || null, no_surname: s.noSurname };

  useEffect(() => { if (cfg && prices.length && !prices.some((p) => p.currency === s.currency)) set({ currency: prices[0].currency, promo: null }); }, [cfg, prices, s.currency]);
  const sel = prices.find((p) => p.currency === s.currency);
  const indiaClosed = !!cfg && sel?.provider === "razorpay" && !cfg.india_open && !(s.promo && Number(s.promo.final) === 0);
  const canNext = [s.first.trim(), s.email && s.email.toLowerCase() === s.email2.toLowerCase() && /\S+@\S+\.\S+/.test(s.email) && giftValid(s) && !indiaClosed, s.confirmed][step];

  const next = async () => {
    if (step === 1 && s.promoInput.trim() && !s.promo) { toast.error("Please click Apply for your promotional code, or clear it."); return; }
    setBusy(true);
    try {
      if (step === 0) await api.post("/name/preview", payload);
      if (step === 1) setPreview((await api.post("/name/preview", payload)).data);
      if (step === 2) {
        setOrder((await api.post("/orders", { ...payload, email: s.email, email_confirm: s.email2, currency: s.currency, confirmed: s.confirmed, promo_code: s.promo?.code || null,
          ...(s.isGift ? { is_gift: true, recipient_email: s.recipientEmail, recipient_email_confirm: s.recipientEmail2, gift_from: s.giftFrom, gift_message: s.giftMessage } : {}) })).data);
        return;
      }
      setStep(step + 1);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <section className="mx-auto grid max-w-6xl gap-14 px-5 py-16 sm:px-8 lg:grid-cols-12">
        <aside className="lg:col-span-4">
          <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#8B5A2B]">Explore your name</p>
          <h1 className="mt-4 font-serif-display text-4xl leading-tight sm:text-5xl">Let's begin with the name you use.</h1>
          <ol className="mt-8 flex flex-wrap gap-x-6 gap-y-3 lg:mt-10 lg:block lg:space-y-4" data-testid="wizard-steps">
            {STEPS.map((t, i) => {
              const cur = order ? STEPS.length : step;
              return (
              <li key={t} className={`flex items-center gap-2 text-sm lg:gap-4 ${i === cur ? "text-stone-900" : "text-stone-400"}`}>
                <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs ${i < cur ? "border-[#B8860B] bg-[#B8860B] text-white" : i === cur ? "border-stone-900" : "border-stone-300"}`}>{i < cur ? "✓" : i + 1}</span>
                {t}
              </li>
            );
            })}
          </ol>
        </aside>
        <div className="lg:col-span-7 lg:col-start-6">
          {order ? <Done order={order} email={s.email} /> : (
            <div key={step} className="fade-up">
              {step === 0 && (
                <div className="space-y-10">
                  <GiftToggle s={s} set={set} />
                  {s.isGift && <p className="-mt-6 text-xs text-[#8B5A2B]" data-testid="gift-name-hint">Enter the recipient's name exactly as they write it.</p>}
                  <StepFirst s={s} set={set} />
                  <StepOther s={s} set={set} />
                  <p className="text-xs leading-relaxed text-stone-500" data-testid="auto-analysis-note">Just enter your name — Namonics™ automatically identifies the letters, vowels, combinations and their positions for you.</p>
                </div>
              )}
              {step === 1 && <StepEmail s={s} set={set} prices={prices} indiaClosed={indiaClosed} />}
              {step === 2 && <StepConfirm s={s} set={set} preview={preview} />}
              <div className="mt-10 flex items-center justify-between gap-4 border-t border-[#E6DEC8] pt-6 sm:mt-12 sm:pt-8">
                <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} className="text-sm text-stone-600 hover:text-stone-900 disabled:invisible" data-testid="wizard-back-btn">← Back</button>
                <button type="button" onClick={next} disabled={!canNext || busy} data-testid="wizard-next-btn"
                  className="group inline-flex items-center gap-3 rounded-full bg-stone-900 px-6 py-3.5 text-sm sm:px-7 font-medium text-[#FDFBF7] transition-colors hover:bg-[#5B2C36] disabled:cursor-not-allowed disabled:opacity-40">
                  {busy ? "Please wait…" : step === 2 ? "Continue to secure payment" : "Continue"} <span className="transition-transform group-hover:translate-x-1">→</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
